import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getEntries, getMonthlyReport } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { el } from 'date-fns/locale';

const monthNames = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαΐ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];

export default function Dashboard() {
  const { selectedFactory } = useAuth();
  const navigate = useNavigate();
  const [recentEntries, setRecentEntries] = useState([]);
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  useEffect(() => {
    if (!selectedFactory) return;
    setLoading(true);
    Promise.all([
      getEntries(selectedFactory.id, { limit: 7 }),
      getMonthlyReport(selectedFactory.id, year, month)
    ]).then(([entriesRes, monthRes]) => {
      setRecentEntries(entriesRes.data);
      setMonthData(monthRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedFactory]);

  if (!selectedFactory) return (
    <div className="page-loading">
      <p>Δεν έχει οριστεί εργοστάσιο</p>
    </div>
  );

  if (loading) return <div className="page-loading"><span className="loading-spinner dark" /> Φόρτωση...</div>;

  const todayStr = format(now, 'yyyy-MM-dd');
  const todayEntry = recentEntries.find(e => e.entry_date?.startsWith(todayStr));
  const chartData = monthData?.daily_breakdown?.map(d => ({
    day: parseInt(d.entry_date?.split('-')[2]),
    total: parseFloat(d.daily_total)
  })) || [];

  return (
    <>
      <div className="page-header">
        <h2>Αρχική — {selectedFactory.name}</h2>
        <p>{format(now, 'EEEE, d MMMM yyyy', { locale: el })}</p>
      </div>
      <div className="page-body">

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-label">Σύνολο Μήνα</div>
            <div className="stat-card-value blue">€{parseFloat(monthData?.grand_total || 0).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</div>
            <div className="stat-card-sub">{monthNames[month - 1]} {year}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Εργάσιμες Μέρες</div>
            <div className="stat-card-value">{monthData?.daily_breakdown?.length || 0}</div>
            <div className="stat-card-sub">Καταχωρήσεις αυτό το μήνα</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Σήμερα</div>
            <div className={`stat-card-value ${todayEntry ? 'green' : ''}`}>
              {todayEntry ? `€${parseFloat(todayEntry.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}` : '—'}
            </div>
            <div className="stat-card-sub">{todayEntry ? 'Έχει καταχωρηθεί' : 'Δεν έχει καταχωρηθεί'}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Μ.Ο. Ημέρας</div>
            <div className="stat-card-value">
              {monthData?.daily_breakdown?.length
                ? `€${(parseFloat(monthData.grand_total) / monthData.daily_breakdown.length).toLocaleString('el-GR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : '—'}
            </div>
            <div className="stat-card-sub">Μέσος όρος αυτό το μήνα</div>
          </div>
        </div>

        <div className="grid-2" style={{ marginBottom: 24 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Παραγωγή {monthNames[month - 1]} {year}</span>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/entry')}>
                + Νέα Καταχώρηση
              </button>
            </div>
            <div className="card-body" style={{ padding: '16px 8px' }}>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={v => `€${v}`} width={55} />
                    <Tooltip formatter={v => [`€${v.toFixed(2)}`, 'Σύνολο']} labelFormatter={l => `Ημέρα ${l}`} />
                    <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-state" style={{ padding: 40 }}>
                  <div>📊</div>
                  <p>Δεν υπάρχουν δεδομένα για αυτό το μήνα</p>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Πρόσφατες Καταχωρήσεις</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/history')}>
                Όλες
              </button>
            </div>
            <div className="table-wrap">
              {recentEntries.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>Ημερομηνία</th>
                      <th>Σύνολο</th>
                      <th>Χρήστης</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentEntries.map(e => (
                      <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/history?date=${format(new Date(e.entry_date), 'yyyy-MM-dd')}`)}>
                        <td>{format(new Date(e.entry_date), 'd MMM yyyy', { locale: el })}</td>
                        <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(e.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</strong></td>
                        <td style={{ color: 'var(--gray-400)', fontSize: 13 }}>{e.submitted_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  <div>📋</div>
                  <p>Δεν υπάρχουν καταχωρήσεις</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {monthData?.product_breakdown?.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Κορυφαία Προϊόντα — {monthNames[month - 1]}</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Προϊόν</th>
                    <th>Κατηγορία</th>
                    <th>Τεμάχια</th>
                    <th>Έσοδα</th>
                  </tr>
                </thead>
                <tbody>
                  {monthData.product_breakdown.slice(0, 8).map((p, i) => (
                    <tr key={i}>
                      <td><strong>{p.product_name}</strong></td>
                      <td><span className="badge badge-blue">{p.category || '—'}</span></td>
                      <td>{parseFloat(p.total_quantity).toLocaleString('el-GR', { maximumFractionDigits: 3 })}</td>
                      <td><strong style={{ color: 'var(--green)' }}>€{parseFloat(p.total_revenue).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
